import { useEffect, useMemo, useState } from 'react';
import { fmtMoney, fmtNum, getLang, localeOf, useT } from '../lib/i18n';
import { createPortal } from 'react-dom';
import { fmtLongDay, fmtTime } from '../lib/time';
import { alert, haptic, openExternal } from '../lib/telegram';
import { MonthCalendar, type CalFocus } from '../components/MonthCalendar';
import { MoneyChart, buildSeries, prevAt, type Range, type Slot } from '../components/MoneyChart';
import { CourtBill } from '../components/CourtBill';
import { MethodBreakdown } from '../components/MethodPicker';
import { LessonPaySheet } from '../components/LessonPayment';

const BASE = import.meta.env.VITE_API_URL ?? '';
let token = '';
export const setStatsToken = (t: string) => { token = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.status === 204 ? (undefined as T) : res.json();
}

interface Day { date: string; earned: number; collected: number; sessions: number; clients: number }
interface Stats {
  days: Day[];
  liability: { credits: number; euros: number };
  owed: { euros: number; sessions: number };
  allTime: { earned: number; sessions: number };
}

const RANGES: Range[] = ['day', 'week', 'month', 'year', 'all'];
const PERIOD_LABEL = {
  day: 'co.money.today', week: 'co.money.thisWeek',
  month: 'co.money.thisMonth', year: 'co.money.thisYear',
} as const;
const SEG_LABEL = {
  day: 'co.money.day', week: 'co.money.week', month: 'co.money.month', year: 'co.money.year',
  all: 'co.money.all',
} as const;

const fmtDate = (key: string, o: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(localeOf(getLang()), { ...o, timeZone: 'UTC' })
    .format(new Date(`${key}T00:00:00Z`)).replace(/\s?г\.$/, '');
const hourOf = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** What a point is called while scrubbing: an hour, a day, a week or a month. */
function pointLabel(s: Slot, t: ReturnType<typeof useT>): string {
  if (s.span === 'hour') return `${t('co.money.today')}, ${hourOf(s.hour!)}`;
  if (s.span === 'week') return t('co.money.weekOf', { date: fmtDate(s.date, { day: 'numeric', month: 'short', year: 'numeric' }) });
  if (s.span === 'month') return fmtDate(s.date, { month: 'long', year: 'numeric' });
  return fmtDate(s.date, { weekday: 'short', day: 'numeric', month: 'long' });
}

/** The compared point, as the badge names it: «к 5 сент.», «к 14:00 вчера». */
function compareLabel(s: Slot, range: Range, change: string, t: ReturnType<typeof useT>): string {
  if (s.span === 'hour') return t('co.money.vsYesterday', { change, time: hourOf(s.hour!) });
  const date = range === 'week'
    ? fmtDate(s.date, { weekday: 'short', day: 'numeric', month: 'short' })
    : range === 'year'
      ? fmtDate(s.date, { day: 'numeric', month: 'short', year: 'numeric' })
      : fmtDate(s.date, { day: 'numeric', month: 'short' });
  return t('co.money.vsPoint', { change, date });
}

/**
 * The obligation, broken down by client.
 *
 * "€1,095 owed" prompts the question "to whom", and until now there was
 * nowhere to look. Shows how far through their package each person is.
 */
function LiabilitySheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [rows, setRows] = useState<any[] | null>(null);

  useEffect(() => {
    api<any[]>('/api/coach/liability/breakdown').then(setRows).catch(() => setRows([]));
  }, []);

  const total = (rows ?? []).reduce((s2, r) => s2 + Number(r.owed), 0);

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.money.heldTitle', { amount: fmtNum(Math.round(total)) })}</div>
        <div className="card__when">{t('co.money.heldHint')}</div>

        {rows === null ? (
          <div className="skeleton" style={{ marginTop: 12 }} />
        ) : rows.length === 0 ? (
          <div className="card__when" style={{ marginTop: 12 }}>{t('co.money.heldNone')}</div>
        ) : (
          <div className="daysheet__list">
            {rows.map((r) => (
              <div className="daysheet__row" key={r.id} style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>
                    {r.name}
                    {r.covers ? <span className="card__when">{t('co.money.alsoCovers', { names: r.covers })}</span> : null}
                  </div>
                  <div className="card__when">
                    {t('co.money.doneOf', {
                      done: r.done, total: Math.round(r.total_sessions), paid: Math.round(r.total_paid),
                    })}
                    {r.booked_ahead > 0 && t('co.money.bookedAhead', { n: Math.round(r.booked_ahead) })}
                    {r.credits_left > 0 && t('co.money.unbooked', { n: Math.round(r.credits_left) })}
                  </div>
                </div>
                <span className="daysheet__time">{fmtMoney(Math.round(r.owed))}</span>
              </div>
            ))}
          </div>
        )}

        <button className="paybar__opt" style={{ width: '100%', marginTop: 14 }}
          onClick={onClose}>{t('co.money.close')}</button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Who owes you, oldest first.
 *
 * Age is the useful sort: a session from three weeks ago is far more
 * likely to have been forgotten than one from yesterday, and it is the
 * one worth a message. Marking it paid from here saves hunting back
 * through the schedule for a day you have already moved past.
 */
function OwedSheet({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const t = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  // A chip opens the lesson's «Оплата» sheet: balance first, then the method.
  const [paying, setPaying] = useState<{ bookingId: string; name: string } | null>(null);

  const load = () => api<any[]>('/api/coach/owed/breakdown').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const total = (rows ?? []).reduce((s2, r) => s2 + Number(r.total), 0);
  const days = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.money.collectTitle', { amount: Math.round(total) })}</div>
        <div className="card__when">{t('co.money.collectHint')}</div>

        {rows === null ? <div className="skeleton" style={{ marginTop: 12 }} /> :
         rows.length === 0 ? (
          <div className="card__when" style={{ marginTop: 14 }}>{t('co.money.nobodyOwes')}</div>
        ) : (
          <div className="daysheet__list">
            {rows.map((r) => (
              <div className="daysheet__row" key={r.client_id} style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>
                    {r.name}{r.offline && <span className="off-tag">{t('co.off.tag')}</span>}
                  </div>
                  <div className="card__when">
                    {t('co.money.sessionsCount', {
                      n: r.sessions, sessions: t.word('co.today.sessions', r.sessions),
                    })}
                    {t('co.money.oldest', {
                      n: days(r.oldest), days: t.word('co.days', days(r.oldest)),
                    })}
                    {r.username && ` · @${r.username}`}
                    {r.offline && r.phone && ` · ${r.phone}`}
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(r.items ?? []).map((it: any) => (
                      <button key={it.bookingId} className="chip"
                        onClick={() => { haptic.tap(); setPaying({ bookingId: it.bookingId, name: r.name }); }}>
                        {t('co.money.gotAmount', {
                          amount: Number(it.amount), day: fmtLongDay(new Date(it.startsAt)),
                        })}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="daysheet__time" style={{ color: 'var(--held)' }}>
                  {fmtMoney(Math.round(r.total))}
                </span>
              </div>
            ))}
          </div>
        )}

        <button className="paybar__opt" style={{ width: '100%', marginTop: 14 }}
          onClick={onClose}>{t('co.money.close')}</button>
        {/* Inside the panel, whose click stops here: closing «Оплата» keeps this list open. */}
        {paying && (
          <LessonPaySheet bookingId={paying.bookingId} name={paying.name}
            onClose={() => setPaying(null)} onDone={() => { load(); onChanged(); }} />
        )}
      </div>
    </div>,
    document.body,
  );
}

/** «Счёт за корт»: a receipt, drawn like the app's other line icons (currentColor). */
const ReceiptIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h12v18l-2.5-1.6L13 21l-2.5-1.6L8 21l-2-1.3z" />
    <path d="M9 8h6M9 11.5h6M9 15h3.5" />
  </svg>
);

export function MoneyTab() {
  const t = useT();
  const [stats, setStats] = useState<Stats | null>(null);
  const [range, setRange] = useState<Range>('month');
  const [pickedDay, setPickedDay] = useState<{ date: string; day: any } | null>(null);
  const [showLiability, setShowLiability] = useState(false);
  const [showOwed, setShowOwed] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [failed, setFailed] = useState(false);
  const [scrub, setScrub] = useState<number | null>(null);
  const [showKept, setShowKept] = useState(true);
  const [showIn, setShowIn] = useState(true);
  const [focus, setFocus] = useState<CalFocus | null>(null);

  useEffect(() => { api<Stats>('/api/coach/stats').then(setStats).catch(() => setFailed(true)); }, []);

  const courtRate = Number((stats as any)?.courtRate ?? 0);
  const built = useMemo(() => stats
    ? buildSeries(stats.days as any, (stats as any).hours ?? [], range, courtRate > 0)
    : null, [stats, range, courtRate]);

  if (failed) return <div className="empty">{t('co.money.failed')}</div>;
  if (!stats || !built) return <div className="list"><div className="skeleton" style={{ height: 180 }} /><div className="skeleton" /></div>;

  // The point on show: the scrubbed one, or now.
  const at = scrub ?? built.upto;
  const point = built.cur[at] ?? { kept: 0, earned: 0, court: 0, collected: 0, sessions: 0, methods: {} };
  const pi = prevAt(built, at);
  const then = pi >= 0 ? built.prev![pi] : null;
  const change = then && then.kept > 0 ? Math.round(((point.kept - then.kept) / then.kept) * 100) : null;
  const nowPi = prevAt(built, built.upto);
  const ahead = nowPi < 0 || (built.cur[built.upto]?.kept ?? 0) > built.prev![nowPi].kept;

  const label = scrub !== null
    ? pointLabel(built.slots[at], t)
    : range === 'all'
      ? t('co.money.sinceStart', { date: fmtDate(built.slots[0].date, { day: 'numeric', month: 'long', year: 'numeric' }) })
      : t(PERIOD_LABEL[range]);

  const tapPoint = (i: number) => {
    const s = built.slots[i];
    setFocus({ date: s.date, span: s.span === 'hour' ? 'day' : s.span, seq: Date.now() });
  };

  return (
    <>
      <div className="mhero">
        <div className="mseg" role="tablist">
          {RANGES.map((p) => (
            <button key={p} role="tab" aria-selected={range === p}
              onClick={() => { haptic.tap(); setScrub(null); setRange(p); }}>
              {t(SEG_LABEL[p])}
            </button>
          ))}
        </div>

        <div className="mhero__label">
          {label}{courtRate > 0 && scrub === null ? t('co.money.afterCourt') : ''}
        </div>
        <div className="mhero__figure">
          {fmtMoney(Math.round(point.kept))}
          {change !== null && then && (
            <span className={`mhero__delta ${change >= 0 ? 'mhero__delta--up' : 'mhero__delta--down'}`}>
              {compareLabel(built.prevSlots[pi], range,
                `${change >= 0 ? '+' : '−'}${fmtNum(Math.abs(change))}`, t)}
            </span>
          )}
        </div>
        <div className="mhero__sub">
          {t('co.money.sessionsCount', {
            n: point.sessions, sessions: t.word('co.today.sessions', point.sessions),
          })}
          {courtRate > 0 && t('co.money.earnedMinusCourt', {
            earned: fmtNum(Math.round(point.earned)), court: fmtNum(Math.round(point.court)),
          })}
        </div>

        <MoneyChart key={range} built={built} range={range} ahead={ahead}
          showKept={showKept} showIn={showIn} onScrub={setScrub} onTap={tapPoint} />

        <div className="mhero__key">
          <button className={`mchip${showKept ? '' : ' mchip--off'}`} aria-pressed={showKept}
            onClick={() => { haptic.tap(); setShowKept(!showKept); }}>
            <i className={`mkey ${ahead ? 'mkey--earned' : 'mkey--behind'}`} />
            {t(courtRate > 0 ? 'co.money.lineNet' : 'co.money.lineEarned')}
          </button>
          <button className={`mchip${showIn ? '' : ' mchip--off'}`} aria-pressed={showIn}
            onClick={() => { haptic.tap(); setShowIn(!showIn); }}>
            <i className="mkey mkey--in" />{t('co.money.lineIn')}
          </button>
          {range !== 'all' && (
            <span className="mchip mchip--static"><i className="mkey mkey--prev" />{t(`co.money.prevKey.${range}`)}</span>
          )}
          <button className="mchart__credit"
            onClick={() => openExternal('https://www.tradingview.com/')}>TradingView</button>
        </div>
      </div>

      {/* «Как получено» for the same point as «Получено» above (scrubbed, or now). */}
      <MethodBreakdown totals={point.methods} />

      <div className="mtiles">
        <button className={`mtile${stats.owed.euros > 0 ? ' mtile--owed' : ''}`}
          onClick={() => { haptic.tap(); setShowOwed(true); }}>
          <span className="mtile__label">{t('co.money.toCollect')}</span>
          <b>{fmtMoney(Math.round(stats.owed.euros))}</b>
          <small>{stats.owed.euros > 0
            ? t('co.money.unpaidSessions', {
                n: stats.owed.sessions, sessions: t.word('co.today.sessions', stats.owed.sessions),
              })
            : t('co.money.allPaidUp')}</small>
        </button>
        <button className="mtile" onClick={() => { haptic.tap(); setShowLiability(true); }}>
          <span className="mtile__label">{t('co.money.paidInAdvance')}</span>
          <b>{fmtMoney(Math.round(stats.liability.euros))}</b>
          <small>{t('co.money.stillToTeach', { n: stats.liability.credits })}</small>
        </button>
      </div>
      <div className="malltime">
        <span>{t('co.money.allTime')}</span>
        <b>{fmtMoney(Math.round(stats.allTime.earned))}</b>
        <span>{t('co.money.sessionsCount', {
          n: stats.allTime.sessions, sessions: t.word('co.today.sessions', stats.allTime.sessions),
        })}</span>
      </div>

      {/* The court bill for a week or a month, to settle with the club (components/CourtBill.tsx). */}
      {(courtRate > 0 || Number((stats as any).allTime?.court ?? 0) > 0) && (
        <button className="cb-entry" onClick={() => { haptic.tap(); setShowBill(true); }}>
          <span className="cb-entry__icon"><ReceiptIcon /></span>
          <span className="cb-entry__main"><b>{t('cb.title')}</b><small>{t('cb.entrySub')}</small></span>
          <span className="setrow__chev" aria-hidden="true">›</span>
        </button>
      )}

      <div className="section-title">{t('co.money.calendar')}</div>
      <div className="mcal">
        <MonthCalendar days={stats.days as any} courtRate={courtRate} focus={focus}
          onPickDay={(date, day) => setPickedDay({ date, day })} />
      </div>

      {showBill && <CourtBill onClose={() => setShowBill(false)} />}
      {showLiability && <LiabilitySheet onClose={() => setShowLiability(false)} />}
      {showOwed && <OwedSheet onClose={() => { setShowOwed(false); location.reload(); }} onChanged={() => {}} />}

      {pickedDay && (
        <DaySheet date={pickedDay.date} day={pickedDay.day}
          courtRate={courtRate}
          onClose={() => setPickedDay(null)} />
      )}
    </>
  );
}

/**
 * One day, opened from the calendar.
 *
 * Past days show what happened. Future days show what's booked and what
 * it would be worth — labelled as a projection throughout, because the
 * temptation to read it as money already made is the whole risk here.
 */
function DaySheet({ date, day, courtRate, onClose }: {
  date: string; day: any; courtRate: number; onClose: () => void;
}) {
  const t = useT();
  const [sessions, setSessions] = useState<any[] | null>(null);
  const future = date > new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api<any[]>(`/api/coach/schedule?from=${date}&to=${date}`)
      .then(setSessions).catch(() => setSessions([]));
  }, [date]);

  const heading = fmtLongDay(new Date(date + 'T12:00:00'));

  const money = future
    ? { gross: day?.projected ?? 0, court: day?.projectedCourt ?? 0, net: day?.projectedNet ?? 0,
        n: day?.projectedSessions ?? 0 }
    : { gross: day?.earned ?? 0, court: day?.court ?? 0, net: day?.net ?? 0, n: day?.sessions ?? 0 };

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{heading}</div>
        <div className="card__when">
          {future ? t('co.money.projected') : t('co.money.whatHappened')}
        </div>

        <div className="totals" style={{ margin: '12px 0 0' }}>
          <div className="totals__row">
            <span>
              {future ? t('co.money.expected') : t('co.money.earned')} ·{' '}
              {t('co.money.sessionsCount', { n: money.n, sessions: t.word('co.today.sessions', money.n) })}
            </span>
            <b>{fmtMoney(Math.round(money.gross))}</b>
          </div>
          {courtRate > 0 && (
            <div className="totals__row totals__row--cost">
              <span>{t('co.cal.courtHire')}</span><b>−{fmtMoney(Math.round(money.court))}</b>
            </div>
          )}
          <div className="totals__row totals__row--net">
            <span>{future ? t('co.money.wouldKeep') : t('co.money.yours')}</span>
            <b>{fmtMoney(Math.round(money.net))}</b>
          </div>
        </div>

        {sessions === null ? (
          <div className="skeleton" style={{ marginTop: 12 }} />
        ) : sessions.length === 0 ? (
          <div className="card__when" style={{ marginTop: 12 }}>{t('co.money.nothingBooked')}</div>
        ) : (
          <div className="daysheet__list">
            {sessions.map((s: any) => (
              <div className="daysheet__row" key={s.id}>
                <span className="daysheet__time">
                  {fmtTime(s.starts_at)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {s.bookings.length === 0 ? t('co.money.free') : s.bookings.map((b: any) => b.name).join(', ')}
                </span>
                <span className="block__price">
                  {s.bookings.map((b: any) =>
                    b.paymentMode === 'cash'
                      ? (b.paidAt ? t('co.money.paid') : fmtMoney(b.amountDue))
                      : t('co.money.credit')).join(', ')}
                </span>
              </div>
            ))}
          </div>
        )}

        <button className="paybar__opt" style={{ width: '100%', marginTop: 14 }}
          onClick={onClose}>{t('co.money.close')}</button>
      </div>
    </div>,
    document.body,
  );
}
